import { Injectable } from '@angular/core';
import { get } from 'lodash';
import { ADPolicyState } from 'src/app/constants/ad-policy.constants';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { DeviceSettingsFeatureToggle } from 'src/app/enums/feature-toggle.enum';
import { IDeviceSettingsDefaultValueService } from '../../device-settings-default-value.interface';
import { DeviceService } from 'src/app/services/device/device.service';
import { EMachineType } from 'src/app/enums/machine-type.enum';
import { IntelligentSecuritySensitivity } from 'src/app/enums/intelligent-security-sensitivity.enum';
import { IntelligentSecurityService } from 'src/app/services/smart-assist/intelligent-security/intelligent-security.service';
import { IntelligentSecurityAIOService } from 'src/app/services/smart-assist/intelligent-security-AIO/intelligent-security-AIO.service';
import { ApplicableType } from 'src/app/enums/intelligent-security-AIO.enum';
import { FeatureToggleService } from '../../../feature-toggle.service';
import { CommonService } from 'src/app/services/common/common.service';
import { LocalStorageKey } from 'src/app/enums/local-storage-key.enum';

@Injectable({
	providedIn: 'root',
})
export class IntelligentSecuritySettingsDefaultValueService
	implements IDeviceSettingsDefaultValueService
{
	constructor(
		private intelligentSecurityService: IntelligentSecurityService,
		private intelligentSecurityServiceAIO: IntelligentSecurityAIOService,
		private logger: LoggerService,
		private commonService: CommonService,
		private deviceService: DeviceService,
		private featureToogleService: FeatureToggleService
	) {}

	public async initialize(adTags: any) {
		try {
			const isIntelligentSecurityAvaiable =
				await this.intelligentSecurityService.isAvailable();
			const AIOHPDCapability = await this.intelligentSecurityServiceAIO.getHPDCapability();
			const machineType: EMachineType = await this.deviceService.getMachineType();
			const isIntelligentSecurityEnableCache = this.commonService.getLocalStorageValue(
				LocalStorageKey.IsIntelligentSecurityEnableCache
			);
			const intelligentSecurity = get(
				adTags,
				DeviceSettingsFeatureToggle.SmartAssistIntelligentSecurity
			);
			const smartAssistAvailable = this.featureToogleService.isPageAvailable('smartAssist');
			const isADPolicyEnabled =
				intelligentSecurity === ADPolicyState.ENABLED || !smartAssistAvailable;
			const isMachineThinkPad = machineType === EMachineType.ThinkPad;
			const thinkPadHPDCapability = isIntelligentSecurityAvaiable && isMachineThinkPad;
			const shouldSetDefaultValues =
				isADPolicyEnabled && (thinkPadHPDCapability || AIOHPDCapability);
			if (shouldSetDefaultValues) {
				this.setDefaultHumanPresenceDetect();
				this.setDefaultZeroTouchLoginStatus();
				this.setDefaultsZeroTouchLockStatus();
				this.turnOFFAdaptDimmingStaus();
			} else {
				if (isIntelligentSecurityEnableCache) {
					this.setDefaultAdaptDimmingStaus();
				}
			}
			this.commonService.setLocalStorageValue(
				LocalStorageKey.IsIntelligentSecurityEnableCache,
				shouldSetDefaultValues
			);
		} catch (error) {
			this.logger.error('setDefaultZeroTouchLoginStatus: ', error);
		}
	}

	private async setDefaultHumanPresenceDetect(): Promise<any> {
		await this.intelligentSecurityService.setHPDStatus(false);
		var privacyGuardCapability =
			await this.intelligentSecurityServiceAIO.getPrivacyGuardCapability();
		if (privacyGuardCapability) {
			await this.intelligentSecurityServiceAIO.setPrivacyGuardStatus(false);
		}
	}

	private async setDefaultZeroTouchLoginStatus(): Promise<any> {
		await this.intelligentSecurityService.setZeroTouchLoginStatus(false);
		await this.intelligentSecurityService.setZeroTouchLoginAdjustStatus(false);
		await this.intelligentSecurityService.setZeroTouchLoginDistance(
			IntelligentSecuritySensitivity.Medium
		);
		var zeroTouchLoginCapability =
			await this.intelligentSecurityServiceAIO.getZeroTouchLoginCapability();
		if (zeroTouchLoginCapability) {
			await this.intelligentSecurityServiceAIO.setZeroTouchLoginStatus(false);
		}
	}

	private async setDefaultsZeroTouchLockStatus(): Promise<any> {
		const selectedLockTimerDeaultValue = IntelligentSecuritySensitivity.Medium.toString();
		await this.intelligentSecurityService.setZeroTouchLockStatus(false);
		await this.intelligentSecurityService.setSelectedLockTimer(selectedLockTimerDeaultValue);
		await this.intelligentSecurityService.setAutoScreenTimeoutStatus(false);
		this.setDefaultHPDLeaveSensitivity();
		var zeroTouchLockCapability =
			await this.intelligentSecurityServiceAIO.getZeroTouchLockCapability();
		if (zeroTouchLockCapability) {
			await this.intelligentSecurityServiceAIO.setZeroTouchLockStatus(false);
		}
	}

	private async setDefaultHPDLeaveSensitivity(): Promise<any> {
		try {
			const hasHPDLeaveSensitivityVisibility =
				await this.intelligentSecurityService.getHPDLeaveSensitivityVisibility();
			if (hasHPDLeaveSensitivityVisibility) {
				await this.intelligentSecurityService.SetHPDLeaveSensitivitySetting(
					IntelligentSecuritySensitivity.Medium
				);
			}
		} catch (error) {
			this.logger.error('setDefaultHPDLeaveSensitivity: ', error);
		}
	}

	private async turnOFFAdaptDimmingStaus(): Promise<any> {
		const hasAdpativeDimmingVisibility = await this.intelligentSecurityService.getATSetting();
		if (hasAdpativeDimmingVisibility) {
			await this.intelligentSecurityService.setHPDATSetting(false);
			await this.intelligentSecurityService.setHPDATTimer(2);
			await this.intelligentSecurityService.setHPDDimTarget(80);
			await this.intelligentSecurityService.setHPDATinAC(true);
		}
	}
	private async setDefaultAdaptDimmingStaus(): Promise<any> {
		const hasAdpativeDimmingVisibility = await this.intelligentSecurityService.getATSetting();
		if (hasAdpativeDimmingVisibility) {
			await this.intelligentSecurityService.setHPDATSetting(true);
			await this.intelligentSecurityService.setHPDATTimer(2);
			await this.intelligentSecurityService.setHPDDimTarget(80);
			await this.intelligentSecurityService.setHPDATinAC(true);
		}
	}
}
